import { type Build, PLAYER_SITE, type Project } from '../lib/api';
import { formatBytes, timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';
import { Chip, cardClass, ErrorNotice, Loading } from './ui';

const STATUS: Record<
  Build['status'],
  { label: string; tone: 'plain' | 'ink' | 'brand' | 'danger' }
> = {
  uploading: { label: 'Uploading', tone: 'plain' },
  processing: { label: 'Unpacking', tone: 'brand' },
  ready: { label: 'Ready', tone: 'ink' },
  failed: { label: 'Failed', tone: 'danger' },
};

/** A project's uploaded builds, newest first, each with a link to play it. */
export function Builds({ project }: { project: Project }) {
  const result = useApi<{ builds: Build[] }>(`/v1/dev/projects/${project.id}/builds`);

  return (
    <section aria-labelledby="builds-heading" className={`${cardClass} px-6 py-5`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="builds-heading" className="text-[15px] font-semibold">
          Builds
        </h2>
        <a
          href={`${PLAYER_SITE}/test/${project.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center font-semibold underline decoration-brand decoration-2 underline-offset-4"
        >
          Open the test community
        </a>
      </div>
      {result.status === 'loading' && <Loading />}
      {result.status === 'error' && (
        <div className="mt-3">
          <ErrorNotice error={result.error} />
        </div>
      )}
      {result.status === 'ready' && result.data.builds.length === 0 && (
        <p className="mt-2 text-sm leading-relaxed text-muted">
          No builds yet. Upload one with the MOGZO CLI; it shows here, ready to play, once it’s
          unpacked.
        </p>
      )}
      {result.status === 'ready' && result.data.builds.length > 0 && (
        <ol className="mt-3">
          {result.data.builds.map((build) => (
            <li
              key={build.id}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-divider py-3.5"
            >
              <span className="w-12 font-mono text-sm font-semibold">#{build.number}</span>
              <span className="min-w-0 flex-[1_1_240px]">
                <span className="flex flex-wrap items-center gap-2">
                  <Chip tone={STATUS[build.status].tone}>{STATUS[build.status].label}</Chip>
                  {build.version && <span className="font-mono text-sm">v{build.version}</span>}
                  <span className="text-sm text-muted">
                    {formatBytes(build.zipBytes)} · {timeAgo(build.createdAt)}
                  </span>
                </span>
                {build.notes && <span className="mt-1 block text-sm">{build.notes}</span>}
                {build.error && (
                  <span className="mt-1 block text-sm text-danger">{build.error}</span>
                )}
                {build.filesDeleted && (
                  <span className="mt-1 block text-sm text-muted">
                    Files removed to save space: only the newest 5 builds stay playable.
                  </span>
                )}
              </span>
              {build.playUrl && (
                <a
                  href={build.playUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center font-semibold underline decoration-brand decoration-2 underline-offset-4"
                >
                  Play build #{build.number}
                </a>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
